import type { Assert, Equals } from '@/lib/api/assert-types'
import { idSchema } from '@/lib/utils'
import z from 'zod/v4'
import type { FloristStatus } from './organizations.types'

/** The statuses people filter florists by: every App\Enums\FloristStatus except non_florist. */
export const floristStatusSchema = z.enum(['needs_setup', 'live', 'lost'])

export type FloristStatusFilter = z.infer<typeof floristStatusSchema>

/** Type-checking fails when the API gains or drops a florist status this list doesn't. */
export type FloristStatusFilterMatchesApi = Assert<
  Equals<FloristStatusFilter, Exclude<FloristStatus, 'non_florist'>>
>

export const organizationSchema = z.object({
  organizationId: idSchema('organization'),
})

export const organizationListSortValues = [
  'name_asc',
  'name_desc',
  'status_asc',
  'status_desc',
  'domain_asc',
  'domain_desc',
  'fsn_client_id_asc',
  'fsn_client_id_desc',
  'phone_asc',
  'phone_desc',
  'email_asc',
  'email_desc',
  'location_asc',
  'location_desc',
  'created_desc',
  'created_asc',
] as const

export const organizationUrlSearchSchema = z
  .object({
    search: z.string().optional(),
    rep: idSchema('rep', true),
    sales_rep: idSchema('sales_rep', true),
    support_rep: idSchema('support_rep', true),
    created_by: idSchema('created_by', true),
    status: floristStatusSchema.optional(),
    sort: z.enum(organizationListSortValues).catch('name_asc'),
    page: z.coerce.number().int().positive().catch(1),
    page_size: z.coerce.number().int().positive().catch(20),
  })
  .strip()

export type OrganizationUrlSearch = z.infer<typeof organizationUrlSearchSchema>

/** The list search params the API filters on (OrganizationFilter); the rest stay client-side. */
export type OrganizationFilters = Partial<
  Pick<OrganizationUrlSearch, 'rep' | 'sales_rep' | 'support_rep' | 'created_by' | 'status'>
>
